import { Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { FiInfo, FiCheckCircle, FiCreditCard, FiShield, FiAlertCircle, FiArrowLeft } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { getBotInfo } from "units/bot/services/botService";
import { charge, customerCharge, paymentGatewayList } from "units/payment/services/paymentServic";

const ClientPaymentGatewayMethod = () => {
    const [loading, setLoading] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState('');
    const [selectedGateway, setSelectedGateway] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0)
    const [walletMessage, setWalletMessage] = useState(0)
    const [list, setList] = useState([])
    const [chargeInfo, setChargeInfo] = useState({})
    const [rules, setRules] = useState(false)
    const [business, setBusiness] = useState("")
    const accessToken = useSelector((state) => state.auth.accessToken);
    const { username } = useParams();
    
    const cleanString = (str) => {
        return str
            .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
            .replace(/[^\d۰-۹٬,]/g, '')
    };

    const convertPersianToEnglish = (str) => {
        const cleaned = cleanString(str);
        return cleaned
            .replace(/[۰-۹]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x6F0))
            .replace(/٬|,/g, '');
    };

    const location = useLocation();
    const finalPrice = location.state?.finalPrice;
    const rawFinalPrice = location.state?.finalPrice || '۰';
    const finalPriceString = convertPersianToEnglish(String(rawFinalPrice));
    const finalPriceNumber = parseInt(finalPriceString, 10);
    const isWalletDisabled = walletBalance < finalPriceNumber;

    const handleSelect = (method, gatewayName = null) => {
        if (method === 'wallet' && isWalletDisabled) return;
        setSelectedMethod(method);
        if (gatewayName) setSelectedGateway(gatewayName);
    };

    useEffect(() => {
        const fetchGateways = async () => {
            try {
                const res = await paymentGatewayList(accessToken)
                if (res?.ok) {
                    setList(res?.data)
                }
            } catch (error) { }
        };
        fetchGateways();
    }, []);
    useEffect(() => {
        const fetchBussinessInfo = async () => {
            try {
                const res = await getBotInfo(username)
                if (res?.ok) {
                    setBusiness(res.data.id);
                }
            } catch (error) { }
        };
        fetchBussinessInfo();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedMethod === 'gateway' && selectedGateway) {
                const res = await customerCharge({
                    accessToken,
                    gateway: selectedGateway,
                    amount: finalPriceNumber,
                    businessId: business
                });
                if (res?.ok) {
                    setChargeInfo(res?.data);
                    window.location.href = res.data.url;
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const formatPrice = (price) => {
        return Number(price).toLocaleString('fa-IR');
    };

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:!from-gray-950 dark:!via-gray-900 dark:!to-indigo-950/40">
                    <ProgressBar
                        visible={true}
                        height="80"
                        width="80"
                        color="#4fa94d"
                        ariaLabel="progress-bar-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                    />
                </div>
            ) : (
                <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:!from-gray-950 dark:!via-gray-900 dark:!to-indigo-950/40">
                    <div className="container mx-auto px-4 py-8 max-w-lg">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-600 dark:!to-teal-800 rounded-2xl shadow-lg dark:!shadow-teal-900/40 mb-4">
                                <FiCreditCard className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-800 dark:!text-gray-100">
                                انتخاب روش پرداخت
                            </h1>
                            <p className="text-gray-500 dark:!text-gray-400 text-sm mt-1">
                                پرداخت امن و سریع
                            </p>
                        </div>

                        {/* Main Card */}
                        <div className="bg-white/80 dark:!bg-gray-900/80 backdrop-blur-sm rounded-3xl shadow-2xl shadow-blue-100/50 dark:!shadow-black/40 overflow-hidden border border-white/50 dark:!border-gray-800">
                            {/* Amount Display */}
                            <div className="bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-700 dark:!to-teal-900 text-white px-6 py-5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-white/90 dark:!text-teal-100/90">
                                        <FiInfo className="w-4 h-4" />
                                        <span className="text-sm font-medium">مبلغ پرداختی</span>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-2xl font-bold text-white">
                                            {formatPrice(finalPriceNumber || 0)}
                                        </span>
                                        <span className="text-sm text-white/70 dark:!text-teal-100/70">ریال</span>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6">
                                {/* Payment Methods */}
                                <div className="space-y-3 mb-6">
                                    <p className="text-xs font-semibold text-gray-400 dark:!text-gray-500 uppercase tracking-wider">
                                        روش پرداخت
                                    </p>

                                    {list && list.length > 0 ? (
                                        list.map((ipgs, index) => (
                                            <label
                                                key={index}
                                                className={`group relative flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 border-2
                                                    ${selectedMethod === 'gateway' && selectedGateway === ipgs
                                                        ? 'border-blue-500 bg-blue-50/80 shadow-lg shadow-blue-100/50 scale-[1.02] dark:!bg-blue-950/40 dark:!border-blue-500 dark:!shadow-blue-900/30'
                                                        : 'border-gray-100 hover:border-blue-200 hover:bg-gray-50/50 hover:scale-[1.01] dark:!border-gray-800 dark:!hover:border-blue-700 dark:!hover:bg-gray-800/60'
                                                    }`}
                                                onClick={() => handleSelect('gateway', ipgs)}
                                            >
                                                <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300
                                                    ${selectedMethod === 'gateway' && selectedGateway === ipgs
                                                        ? 'border-blue-500 bg-blue-500'
                                                        : 'border-gray-300 group-hover:border-blue-300 dark:!border-gray-600 dark:!group-hover:border-blue-600'
                                                    }`}>
                                                    {selectedMethod === 'gateway' && selectedGateway === ipgs && (
                                                        <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className="w-12 h-12 rounded-xl bg-gray-50 dark:!bg-gray-800 flex items-center justify-center overflow-hidden">
                                                        <img
                                                            style={{ width: "50px", height: "50px", objectFit: "contain" }}
                                                            src={`${ipgs === "zarinpal" ? "/Zarrinpal-Logo.png" : "/zibal.png"}`}
                                                            alt='logo'
                                                        />
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-700 dark:!text-gray-200">
                                                            پرداخت از طریق {ipgs === "zarinpal" ? "زرین‌پال" : "زیبال"}
                                                        </span>
                                                        <p className="text-xs text-gray-400 dark:!text-gray-500">درگاه بانکی</p>
                                                    </div>
                                                </div>

                                                {selectedMethod === 'gateway' && selectedGateway === ipgs && (
                                                    <div className="flex-shrink-0">
                                                        <span className="text-xs font-medium text-blue-600 dark:!text-blue-300 bg-blue-100 dark:!bg-blue-900/50 px-2 py-1 rounded-full">
                                                            انتخاب شده
                                                        </span>
                                                    </div>
                                                )}
                                            </label>
                                        ))
                                    ) : (
                                        <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50 dark:!bg-amber-950/40 border-2 border-amber-200 dark:!border-amber-800/60">
                                            <FiAlertCircle className="w-5 h-5 text-amber-500 dark:!text-amber-400 flex-shrink-0" />
                                            <span className="text-sm text-amber-700 dark:!text-amber-300">
                                                در حال حاضر هیچ درگاه بانکی فعالی موجود نیست
                                            </span>
                                        </div>
                                    )}

                                    {/* Wallet Option */}
                                    <label
                                        className={`group relative flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 border-2
                                            ${selectedMethod === 'wallet'
                                                ? 'border-emerald-500 bg-emerald-50/80 shadow-lg shadow-emerald-100/50 scale-[1.02] dark:!bg-emerald-950/40 dark:!border-emerald-500 dark:!shadow-emerald-900/30'
                                                : 'border-gray-100 hover:border-emerald-200 hover:bg-gray-50/50 hover:scale-[1.01] dark:!border-gray-800 dark:!hover:border-emerald-700 dark:!hover:bg-gray-800/60'
                                            }
                                            ${isWalletDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        onClick={() => handleSelect('wallet')}
                                    >
                                        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300
                                            ${selectedMethod === 'wallet'
                                                ? 'border-emerald-500 bg-emerald-500'
                                                : 'border-gray-300 group-hover:border-emerald-300 dark:!border-gray-600 dark:!group-hover:border-emerald-600'
                                            }`}>
                                            {selectedMethod === 'wallet' && (
                                                <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:!bg-emerald-950/50 flex items-center justify-center">
                                                <Wallet className="w-5 h-5 text-emerald-600 dark:!text-emerald-400" />
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-700 dark:!text-gray-200">
                                                    پرداخت از کیف پول
                                                </span>
                                                <p className="text-xs text-gray-400 dark:!text-gray-500">
                                                    موجودی: {formatPrice(walletBalance)} ریال
                                                </p>
                                            </div>
                                        </div>

                                        {isWalletDisabled ? (
                                            <span className="text-xs text-red-500 dark:!text-red-400 font-medium">موجودی ناکافی</span>
                                        ) : selectedMethod === 'wallet' && (
                                            <span className="text-xs font-medium text-emerald-600 dark:!text-emerald-300 bg-emerald-100 dark:!bg-emerald-900/50 px-2 py-1 rounded-full">
                                                انتخاب شده
                                            </span>
                                        )}
                                    </label>
                                </div>

                                {/* Terms */}
                                <div className="border-t border-gray-100 dark:!border-gray-800 pt-5 mb-6">
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={rules}
                                            onChange={() => setRules(!rules)}
                                            className="mt-0.5 w-5 h-5 rounded-lg border-2 border-gray-300 dark:!border-gray-600 dark:!bg-gray-800 text-blue-600 focus:ring-2 focus:ring-blue-400 dark:!focus:ring-blue-600 focus:ring-offset-2 dark:!focus:ring-offset-gray-900 cursor-pointer transition-all"
                                        />
                                        <div className="flex items-start gap-2">
                                            <FiShield className="w-4 h-4 text-gray-400 dark:!text-gray-500 mt-0.5 group-hover:text-blue-500 dark:!group-hover:text-blue-400 transition-colors" />
                                            <span className="text-sm text-gray-600 dark:!text-gray-300 leading-relaxed">
                                                تمامی <span className="text-blue-600 dark:!text-blue-400 font-medium">شرایط و مقررات</span> سایت را می‌پذیرم
                                            </span>
                                        </div>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type='submit'
                                    disabled={!selectedMethod || (selectedMethod === "ipg" && !selectedGateway) || !rules}
                                    className={`w-full py-3.5 rounded-2xl text-center font-semibold transition-all duration-300 relative overflow-hidden
                                        ${!selectedMethod || !rules
                                            ? 'bg-gray-100 dark:!bg-gray-800 text-gray-400 dark:!text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:!from-blue-600 dark:!to-indigo-700 text-white hover:shadow-xl hover:shadow-blue-200/50 dark:!hover:shadow-blue-900/40 hover:scale-[1.02] active:scale-[0.98]'
                                        }`}
                                >
                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                        {!selectedMethod || !rules ? (
                                            <>
                                                <FiAlertCircle className="w-4 h-4" />
                                                تکمیل اطلاعات الزامی است
                                            </>
                                        ) : (
                                            <>
                                                <FiArrowLeft className="w-4 h-4" />
                                                پرداخت نهایی
                                            </>
                                        )}
                                    </span>
                                    {!selectedMethod || !rules ? null : (
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 dark:!from-blue-500 dark:!to-indigo-600 opacity-0 hover:opacity-100 transition-opacity duration-300" />
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Footer */}
                        <div className="mt-6 text-center">
                            <p className="text-xs text-gray-400 dark:!text-gray-500">
                                🔒 پرداخت شما با امنیت کامل انجام می‌شود
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
export default ClientPaymentGatewayMethod;